import { useEffect, useMemo, useState } from "react";
import { X, User, FileText, Clock, Building2, Kanban, Radar, Pencil, Trash2, UserCheck, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Btn, Input, fmtBRL, TabBar, ExportButtons } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import Pipeline from "@/pages/Pipeline";
import TendersRadar from "@/pages/TendersRadar";

const sentimentTone = (s) => (s === "positivo" ? "pos" : s === "negativo" ? "neg" : "muted");

const STAGE_LABEL = { new: "Novo", qualifying: "Qualificação", proposal: "Proposta", negotiation: "Negociação", won: "Ganho", lost: "Perdido" };
const STAGE_TONE = { won: "pos", lost: "neg", negotiation: "primary", proposal: "primary" };

const selectCls = "h-9 rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

function EditAccountModal({ account, onClose, onSaved }) {
  const [form, setForm] = useState({ name: account.name || "", segment: account.segment || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome da conta");
    setBusy(true);
    try {
      const { data } = await api.patch(`/accounts/${account.id}`, { name: form.name.trim(), segment: form.segment.trim() });
      toast.success("Conta atualizada");
      onSaved(data.account);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-account-modal">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-zinc-900">Editar conta</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">Nome da conta</label>
            <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-account-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Segmento</label>
            <Input className="mt-1" value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} placeholder="Ex.: Órgão público, Varejo…" data-testid="edit-account-segment" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={busy} data-testid="edit-account-save">{busy ? "Salvando…" : "Salvar"}</Btn>
        </div>
      </Card>
    </div>
  );
}

function Contas() {
  const [accounts, setAccounts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOwner, setBulkOwner] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/accounts").then(({ data }) => setAccounts(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmps((data || []).filter((e) => e.status === "active"))).catch(() => {});
  }, []);
  const open = async (id) => { const { data } = await api.get(`/accounts/${id}/timeline`); setDetail(data); };

  const del = async (a) => {
    if (!window.confirm(`Excluir a conta "${a.name}"? Isto remove a oportunidade, contatos, histórico e conhecimento vinculados. Não dá para desfazer.`)) return;
    try {
      await api.delete(`/accounts/${a.id}`);
      toast.success(`Conta "${a.name}" excluída`);
      setAccounts((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const assign = async (accountIds, ownerId) => {
    if (!accountIds.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/accounts/assign", { account_ids: accountIds, owner_id: ownerId || null });
      const oname = data.owner_name || null;
      const oid = data.owner_id || null;
      toast.success(oid ? `Atribuído a ${oname} (${accountIds.length})` : `Marcado como sem responsável (${accountIds.length})`);
      setAccounts((prev) => prev.map((a) => (accountIds.includes(a.id) ? { ...a, owner_id: oid, owner_name: oname } : a)));
      setSelected(new Set());
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const filtered = useMemo(() => accounts.filter((a) => {
    if (stageFilter === "none") { if (a.opportunity) return false; }
    else if (stageFilter !== "all") { if ((a.opportunity?.stage || "") !== stageFilter) return false; }
    if (ownerFilter === "unassigned") { if (a.owner_id) return false; }
    else if (ownerFilter !== "all") { if (a.owner_id !== ownerFilter) return false; }
    return true;
  }), [accounts, stageFilter, ownerFilter]);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisibleSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(() => (allVisibleSelected ? new Set() : new Set(filtered.map((a) => a.id))));

  return (
    <>
      {/* Toolbar: contagem + filtros + export */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-zinc-500" data-testid="accounts-count">{filtered.length} de {accounts.length} conta(s)</p>
          <select className={selectCls} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="filter-stage">
            <option value="all">Todos os status</option>
            {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            <option value="none">Sem oportunidade</option>
          </select>
          <select className={selectCls} value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} data-testid="filter-owner">
            <option value="all">Todos os responsáveis</option>
            <option value="unassigned">Sem responsável</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <ExportButtons basePath="/accounts/export" name="contas" testid="accounts-export" />
      </div>

      {/* Barra de atribuição em massa */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-2.5">
        <button onClick={toggleAll} className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900" data-testid="select-all">
          {allVisibleSelected ? <CheckSquare size={16} className="text-indigo-600" /> : <Square size={16} />}
          {allVisibleSelected ? "Limpar seleção" : "Selecionar todos"}
        </button>
        <span className="text-sm text-zinc-400">·</span>
        <span className="text-sm text-zinc-600" data-testid="selected-count">{selected.size} selecionado(s)</span>
        <div className="ml-auto flex items-center gap-2">
          <UserCheck size={16} className="text-zinc-400" />
          <select className={selectCls} value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)} disabled={!selected.size} data-testid="bulk-owner-select">
            <option value="">Atribuir a…</option>
            <option value="__none__">Sem responsável</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <Btn onClick={() => assign([...selected], bulkOwner === "__none__" ? null : bulkOwner)}
            disabled={!selected.size || !bulkOwner || busy} data-testid="bulk-assign-btn">
            {busy ? "Atribuindo…" : "Atribuir"}
          </Btn>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <Card key={`sk-${i}`} className="animate-pulse p-4" data-testid="account-skeleton">
            <div className="h-4 w-2/3 rounded bg-zinc-100" />
            <div className="mt-2 h-3 w-1/3 rounded bg-zinc-100" />
            <div className="mt-4 h-6 w-1/2 rounded bg-zinc-100" />
            <div className="mt-4 h-7 w-full rounded bg-zinc-100" />
          </Card>
        ))}
        {!loading && filtered.map((a) => {
          const isSel = selected.has(a.id);
          return (
            <Card key={a.id} className={`group relative p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${isSel ? "ring-2 ring-indigo-400" : ""}`} data-testid={`account-${a.id}`}>
              <button onClick={(e) => { e.stopPropagation(); toggle(a.id); }} data-testid={`account-check-${a.id}`}
                className="absolute left-2 top-2 z-10 rounded-md p-1 text-zinc-400 hover:text-indigo-600" title="Selecionar">
                {isSel ? <CheckSquare size={16} className="text-indigo-600" /> : <Square size={16} />}
              </button>
              <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button onClick={(e) => { e.stopPropagation(); setEditing(a); }} data-testid={`account-edit-${a.id}`}
                  className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-indigo-600" title="Editar conta"><Pencil size={14} /></button>
                <button onClick={(e) => { e.stopPropagation(); del(a); }} data-testid={`account-delete-${a.id}`}
                  className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600" title="Excluir conta"><Trash2 size={14} /></button>
              </div>
              <div className="cursor-pointer pl-6" onClick={() => open(a.id)}>
                <div className="flex items-center justify-between gap-2 pr-14">
                  <p className="font-display font-semibold text-zinc-900">{a.name}</p>
                  {a.opportunity && <Badge tone={STAGE_TONE[a.opportunity.stage] || "muted"}>{STAGE_LABEL[a.opportunity.stage] || a.opportunity.stage}</Badge>}
                </div>
                <p className="mt-1 text-xs text-zinc-500">{a.segment || "—"}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-display text-lg font-semibold text-zinc-900">{fmtBRL(a.opportunity?.value)}</span>
                  <span className="text-xs text-zinc-400">{a.interactions_count} interações</span>
                </div>
              </div>
              {/* Responsável + atribuir inline */}
              <div className="mt-3 flex items-center gap-2 border-t border-zinc-100 pt-3">
                {a.owner_id
                  ? <Badge tone="primary"><UserCheck size={11} className="mr-1 inline" />{a.owner_name}</Badge>
                  : <Badge tone="neg">Sem responsável</Badge>}
                <select className={`${selectCls} ml-auto h-8 max-w-[8.5rem] py-0 text-xs`} value="" disabled={busy}
                  onChange={(e) => { const v = e.target.value; if (v) assign([a.id], v === "__none__" ? null : v); }}
                  data-testid={`assign-select-${a.id}`}>
                  <option value="">Atribuir…</option>
                  <option value="__none__">Sem responsável</option>
                  {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </Card>
          );
        })}
        {!loading && !filtered.length && (
          <p className="text-sm text-zinc-500">
            {accounts.length ? "Nenhuma conta com esses filtros." : "Você ainda não tem contas cadastradas."}
          </p>
        )}
      </div>

      {editing && (
        <EditAccountModal account={editing} onClose={() => setEditing(null)}
          onSaved={(acc) => { setEditing(null); setAccounts((prev) => prev.map((x) => x.id === acc.id ? { ...x, ...acc } : x)); }} />
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-zinc-900/40" onClick={() => setDetail(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-zinc-200 bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="account-drawer">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-zinc-900">{detail.account?.name}</h2>
              <button onClick={() => setDetail(null)} data-testid="close-drawer" className="text-zinc-400 hover:text-zinc-900"><X size={20} /></button>
            </div>
            {detail.account && (
              <p className="mb-4 text-xs text-zinc-500">
                Responsável: {detail.account.owner_id ? <span className="font-medium text-zinc-700">{detail.account.owner_name}</span> : <span className="font-medium text-red-500">Sem responsável</span>}
              </p>
            )}

            {detail.contacts?.length > 0 && (
              <div className="mb-5">
                <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400"><User size={14} /> Contatos</p>
                {detail.contacts.map((c) => <p key={c.id} className="text-sm text-zinc-700">{c.name} <span className="text-zinc-400">— {c.role || ""}</span></p>)}
              </div>
            )}

            {detail.documents?.length > 0 && (
              <div className="mb-5">
                <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400"><FileText size={14} /> Documentos</p>
                {detail.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700">
                    <span>{d.title}</span>
                    <span className="flex items-center gap-2">
                      <Badge>v{d.version}</Badge>
                      <Badge tone={d.status === "current" ? "pos" : "muted"}>{d.status}</Badge>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400"><Clock size={14} /> Linha do tempo</p>
            <div className="relative space-y-4 border-l border-zinc-200 pl-5">
              {detail.timeline?.map((it) => (
                <div key={it.id} className="relative">
                  <span className="absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full bg-indigo-600" />
                  <div className="flex items-center gap-2">
                    <Badge>{it.type}</Badge>
                    <Badge tone={it.origin === "ai" ? "primary" : "muted"}>{it.origin}</Badge>
                    {it.payload?.sentiment && <Badge tone={sentimentTone(it.payload.sentiment)}>{it.payload.sentiment}</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-zinc-700">{it.payload?.summary || it.payload?.answer || it.payload?.text}</p>
                  <p className="mt-1 text-xs text-zinc-400">{it.author_name} · {String(it.created_at).slice(0, 16).replace("T", " ")}</p>
                </div>
              ))}
              {!detail.timeline?.length && <p className="text-sm text-zinc-500">Sem registros</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Accounts() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("contas");
  const showTenders = tenant?.features?.tenders !== false;
  const tabs = [
    { key: "contas", label: "Contas", icon: Building2 },
    { key: "pipeline", label: "Pipeline", icon: Kanban },
    ...(showTenders ? [{ key: "licitacoes", label: "Licitações", icon: Radar }] : []),
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Comercial</h1>
        <p className="mt-1 text-sm text-zinc-500">Suas contas, oportunidades e o funil de vendas.</p>
      </div>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "contas" && <Contas />}
      {tab === "pipeline" && <Pipeline embedded />}
      {tab === "licitacoes" && showTenders && <TendersRadar embedded />}
    </div>
  );
}
